import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import distributorService from '../services/distributorService';
import CashCardFields, { emptyCashCard } from '../components/distributor-cash/CashCardFields';
import { tokenize, validateCard, tokenizationErrorMessage } from '../utils/intuitCardToken';
import { captureSession, isSessionCurrent, SESSION_EVENT } from '../utils/authSession';
import '../components/distributor-cash/CashBank.css';
import './AdminDistributorPaymentTest.css';

export const VERIFICATION_CONSENT =
  'I authorize MedEffects to charge my card exactly $2.00 USD for this one-time payment verification. MedEffects covers processing fees. This test does not add distributor credit.';
const tokenUrl = 'https://api.intuit.com/quickbooks/v4/payments/tokens';
export function readVerification(response) {
  const row = response?.data?.data;
  if (
    !row ||
    row.version !== 1 ||
    row.amountCents !== 200 ||
    row.currency !== 'USD' ||
    row.consent !== VERIFICATION_CONSENT ||
    row.balanceCreditCents !== 0 ||
    row.accountingPosted !== false ||
    typeof row.ready !== 'boolean' ||
    typeof row.attempted !== 'boolean' ||
    !['not_prepared', 'prepared', 'processing', 'observed', 'unknown'].includes(row.state) ||
    (row.ready && (row.state !== 'prepared' || row.attempted || row.tokenUrl !== tokenUrl)) ||
    (row.providerStatus !== null &&
      !['AUTHORIZED', 'DECLINED', 'CAPTURED', 'CANCELLED', 'SETTLED', 'REFUNDED'].includes(
        row.providerStatus
      ))
  )
    throw new Error('Invalid test response');
  return row;
}
export default function AdminDistributorPaymentTest() {
  const [record, setRecord] = useState(null),
    [card, setCard] = useState(emptyCashCard),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const session = useRef(captureSession()),
    mounted = useRef(false),
    pending = useRef(false),
    sent = useRef(false);
  const current = useCallback(() => mounted.current && isSessionCurrent(session.current), []);
  const load = useCallback(async () => {
    const value = readVerification(await distributorService.cardVerification());
    if (current()) {
      setRecord(value);
      sent.current = value.attempted;
    }
    return value;
  }, [current]);
  useEffect(() => {
    mounted.current = true;
    const clear = () => {
      if (!current()) {
        setCard(emptyCashCard());
        setRecord(null);
      }
    };
    window.addEventListener(SESSION_EVENT, clear);
    window.addEventListener('storage', clear);
    load().catch(() => {
      if (current())
        setError(
          'The payment test could not be loaded. Reload its saved status before continuing.'
        );
    });
    return () => {
      mounted.current = false;
      window.removeEventListener(SESSION_EVENT, clear);
      window.removeEventListener('storage', clear);
    };
  }, [current, load]);
  const run = async (work) => {
    if (pending.current || !current()) return;
    pending.current = true;
    setBusy(true);
    setError('');
    setCard(emptyCashCard());
    try {
      const row = readVerification(await work());
      if (current()) {
        setRecord(row);
        sent.current = row.attempted;
      }
    } catch {
      if (current())
        setError(
          'Could not confirm the saved test. Refresh its status; do not submit another payment.'
        );
    } finally {
      pending.current = false;
      if (current()) setBusy(false);
    }
  };
  const pay = async (event) => {
    event.preventDefault();
    if (!current() || pending.current || sent.current || !record?.ready) return;
    const problem = validateCard(card);
    if (problem) return setError(problem);
    const details = { ...card };
    pending.current = true;
    setBusy(true);
    setError('');
    setCard(emptyCashCard());
    let dispatched = false;
    try {
      const before = await load();
      if (!current() || !before.ready || before.attempted) throw new Error();
      const token = await tokenize(before.tokenUrl, details);
      if (!current()) return;
      const after = await load();
      if (!current() || !after.ready || after.attempted) throw new Error();
      sent.current = true;
      dispatched = true;
      const value = readVerification(
        await distributorService.payCardVerification({
          token,
          authorized: true,
          amountCents: 200,
          currency: 'USD',
          authorizationVersion: 1,
        })
      );
      if (current()) setRecord(value);
    } catch (failure) {
      if (!current()) return;
      if (dispatched) {
        try {
          await load();
        } catch {}
        setError(
          'The payment outcome needs review. Refresh the saved status; do not send another payment.'
        );
      } else
        setError(
          tokenizationErrorMessage(failure) ||
            'Payment readiness changed. No test payment was submitted. Reload the saved status.'
        );
    } finally {
      pending.current = false;
      if (current()) setBusy(false);
    }
  };
  return (
    <main className="me-cash me-payment-verification" aria-label="Distributor payment verification">
      <Link to="/admin/distributor-bank">← Back to Tissue Bank</Link>
      <header>
        <p>Administrator setup</p>
        <h1>One-time card verification</h1>
        <p>
          Andrew’s $2.00 test verifies the portal’s QuickBooks payment connection before distributor
          funding is enabled.
        </p>
      </header>
      <fieldset className="me-cash-panel">
        <legend>$2.00 USD · one payment only</legend>
        <p>
          MedEffects covers processing fees. This test leaves Kevin and Carole’s balance unchanged.
          We will verify the charge and bookkeeping before enabling funding.
        </p>
        {error && <p role="alert">{error}</p>}
        {record && (
          <p role="status">
            {record.attempted
              ? `Saved payment status: ${record.providerStatus || (record.state === 'processing' ? 'Processing' : 'Needs review')}.`
              : 'No payment has been submitted.'}
          </p>
        )}
        {record?.state === 'not_prepared' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => run(distributorService.prepareCardVerification)}
          >
            Prepare $2.00 test
          </button>
        )}
        {record?.ready && !record.attempted && (
          <form onSubmit={pay}>
            <CashCardFields
              card={card}
              edit={(key, value) => setCard((previous) => ({ ...previous, [key]: value }))}
              disabled={busy || sent.current}
              amountCents={200}
              authorizationText={VERIFICATION_CONSENT}
              authorizationLabel="Authorize the one-time $2.00 test"
            />
            <p>
              Card details go directly to Intuit. MedEffects does not store your card number or
              security code.
            </p>
            <button type="submit" disabled={busy || sent.current || !card.authorized}>
              {busy ? 'Checking payment…' : 'Pay $2.00 once'}
            </button>
          </form>
        )}
        {record?.state === 'prepared' && !record.ready && (
          <p>
            The test is not enabled or its connection needs review. No card payment can be
            submitted.
          </p>
        )}
        {record?.attempted && (
          <p>
            The payment cannot be submitted again. Captured or settled status does not add Tissue
            Bank credit.
          </p>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={() => run(distributorService.refreshCardVerification)}
        >
          Refresh saved status
        </button>
      </fieldset>
    </main>
  );
}
